import { h, render } from 'preact';
import Drawer from './drawer';
import { css } from '@emotion/react';

import { useOrientation } from '@/hooks/useOrientation';
import { CacheProvider } from '@emotion/react';
import { resources as i18nResources } from '@/lib/i18n';
import { LightIntlProvider } from '@/lib/i18n/i18n-provider';
import { getDetectedLanguage } from './utils/detect-language';
import { modalIframeManager } from '@/components/utils/singleton-iframe';
import createCache from '@emotion/cache';

export type ModalPlacement = 'top' | 'end' | 'bottom' | 'start' | 'center';

export interface ModalOptions {
  title: string;
  content: h.JSX.Element | HTMLElement;
  height?: number | string;
  placement?: ModalPlacement;
  noHeader?: boolean;
  padding?: string;
  onClose?: (...args: any[]) => void;
  onOpen?: (...args: any[]) => void;
  preventCloseOnBackdropClick?: boolean;
  backdropColor?: string;
  footerButtons?: Array<{
    text: string;
    variant?: string;
    onClick?: (e: MouseEvent, utils: { hide: () => void }) => void;
  }>;
}

const ModalContent = ({
  title,
  content,
  noHeader,
  height,
  padding = '16px',
  footerButtons,
  onClose
}: ModalOptions) => {
  const orientation = useOrientation();
  const styles = {
    container: css`
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      max-width: ${orientation === 'portrait' ? '440px' : '75%'};
      margin: 0 auto;
    `,
    header: css`
      padding: 16px;
      border-bottom: 1px solid #eee;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `,
    content: css`
      padding: ${padding};
      flex: 1;
      overflow: auto;
    `,
    footer: css`
      padding: 16px;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    `,
    closeButton: css`
      background: none;
      border: none;
      cursor: pointer;
      font-size: 20px;
    `,
    button: css`
      padding: 8px 16px;
      border-radius: 4px;
      border: 1px solid #ddd;
      cursor: pointer;
    `
  };

  return (
    <div className={styles.container.name}>
      {!noHeader && (
        <div className={styles.header.name}>
          <h3>{title}</h3>
          <button className={styles.closeButton.name} onClick={onClose}>
            ×
          </button>
        </div>
      )}
      <div className={styles.content.name}>{content}</div>
      {footerButtons && footerButtons.length > 0 && (
        <div className={styles.footer.name}>
          {footerButtons.map((button, index) => (
            <button
              key={index}
              className={
                css`
                  ${styles.button}
                  background-color: ${button.variant === 'primary' ? '#1677ff' : '#fff'};
                  color: ${button.variant === 'primary' ? '#fff' : '#333'};
                `.name
              }
              onClick={(e) => button.onClick?.(e as MouseEvent, { hide: onClose! })}
            >
              {button.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const createModal = (options: Omit<ModalOptions, 'dynamicCss'>) => {
  let drawerInstance: any = null;
  let currentModalIsOpen = false;

  const language = getDetectedLanguage();
  const currentLanguage = i18nResources[language as keyof typeof i18nResources].translations;

  const containerId = `drawer-container-${Math.random().toString(36).slice(2, 10)}`;
  let iframe: HTMLIFrameElement | null = null;
  let emotionCache: ReturnType<typeof createCache> | null = null;
  let drawerContainer: HTMLElement | null = null;

  function renderOrUpdateDrawer(showDrawer: boolean) {
    if (!iframe || !emotionCache) {
      console.error('[Modal] Iframe or emotion cache not ready for rendering Drawer.');
      return;
    }

    // 多实例: 每个modal有独立container
    drawerContainer = iframe.contentDocument!.getElementById(containerId);
    if (!drawerContainer) {
      drawerContainer = iframe.contentDocument!.createElement('div');
      drawerContainer.id = containerId;
      iframe.contentDocument!.body.appendChild(drawerContainer);
    }

    render(
      <CacheProvider value={emotionCache}>
        <LightIntlProvider definition={currentLanguage}>
          <Drawer
            ref={(instance: any) => {
              drawerInstance = instance;
            }}
            placement={options.placement ?? 'bottom'}
            isOpen={showDrawer}
            onClose={handleInternalClose}
            preventCloseOnBackdropClick={options.preventCloseOnBackdropClick ?? false}
            backdropColor={options.backdropColor}
          >
            <ModalContent {...options} onClose={hideModal} />
          </Drawer>
        </LightIntlProvider>
      </CacheProvider>,
      drawerContainer
    );
  }

  function handleInternalClose() {
    if (!currentModalIsOpen) {
      return;
    }
    currentModalIsOpen = false;

    if (iframe?.contentDocument) {
      const container = iframe.contentDocument.getElementById(containerId);
      if (container) {
        container.parentNode?.removeChild(container);
      }
    }

    modalIframeManager.release();
    options.onClose?.();
  }

  async function showModal() {
    // Should not happen if getOrCreatePersistentIframe was called
    const acquired = await modalIframeManager.acquire();
    iframe = acquired.iframe;
    emotionCache = acquired.emotionCache;

    currentModalIsOpen = true;
    renderOrUpdateDrawer(true);

    setTimeout(() => {
      options.onOpen?.();
    }, 50);
  }

  function hideModal() {
    if (!currentModalIsOpen) {
      return;
    }
    renderOrUpdateDrawer(false);
  }

  return {
    show: () => {
      if (!currentModalIsOpen) {
        // Prevent multiple show calls if already open
        showModal();
      }
    },
    hide: () => {
      if (currentModalIsOpen) {
        hideModal();
      }
    },
    isOpen: () => currentModalIsOpen,
    destroy: () => {
      if (currentModalIsOpen) {
        hideModal();
      }
      drawerInstance = null;
      iframe = null;
      emotionCache = null;
      drawerContainer = null;
      console.log('[Modal] Instance destroyed.');
    }
  };
};
